/**
 * Mobile Money Account Dropdown/Accordion Component
 * Shows last used account as default with dropdown to select other accounts or add new
 */

import { formatPhoneNumber } from '@/utils/format'
import type { MobileMoneyAccount } from '@/types'
import { NewMobileMoneyForm } from './NewMobileMoneyForm'

interface MobileMoneyDropdownProps {
  accounts: MobileMoneyAccount[]
  lastUsed: MobileMoneyAccount | null
  selectedAccount: MobileMoneyAccount | null
  onSelect: (account: MobileMoneyAccount) => void
  onAddNew: (data: { operator: string; mobile_number: string }) => Promise<void>
  isLoading?: boolean
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  showAddForm: boolean
  setShowAddForm: (showAddForm: boolean) => void
}

export function MobileMoneyDropdown({
  accounts,
  lastUsed,
  selectedAccount,
  onSelect,
  onAddNew,
  isLoading,
  isOpen,
  setIsOpen,
  showAddForm,
  setShowAddForm,
}: MobileMoneyDropdownProps) {

  const getNetworkLogo = (operator: string) => {
    const operatorUpper = operator.toUpperCase()
    if (operatorUpper === 'MTN') {
      return (
        <div className="w-12 h-12 bg-yellow-400 rounded-lg flex items-center justify-center flex-shrink-0">
          <span className="text-black font-bold text-sm">MTN</span>
        </div>
      )
    } else if (operatorUpper === 'TELECEL') {
      return (
        <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-xs">TELECEL</span>
        </div>
      )
    } else if (operatorUpper === 'AIRTELTIGO') {
      return (
        <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-xs">AIRTEL</span>
        </div>
      )
    }
    return (
      <div className="w-12 h-12 bg-gray-400 rounded-lg flex items-center justify-center flex-shrink-0">
        <span className="text-white font-bold text-xs">{operator}</span>
      </div>
    )
  }

  const displayAccount = selectedAccount || lastUsed
  const otherAccounts = accounts.filter(
    (acc) => acc.id !== (displayAccount?.id || -1)
  )

  const handleAddNew = async (data: { operator: string; mobile_number: string }) => {
    try {
      console.log('[MobileMoneyDropdown] handleAddNew called with:', data)
      console.log('[MobileMoneyDropdown] Calling onAddNew prop...')
      await onAddNew(data)
      console.log('[MobileMoneyDropdown] onAddNew completed successfully')
      setShowAddForm(false)
      setIsOpen(false)
    } catch (error) {
      // Error is already handled in PaymentForm, just keep form open
      console.error('[MobileMoneyDropdown] Error in handleAddNew:', error)
    }
  }

  if (!displayAccount && accounts.length === 0) {
    return null
  }

  return (
    <div className="space-y-3">
      {/* Selected/Last Used Account Display */}
      {displayAccount && (
        <div className="flex items-center space-x-3 p-3 rounded-lg border-2 border-gray-200 bg-white">
          {getNetworkLogo(displayAccount.operator)}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900">
              {formatPhoneNumber(displayAccount.mobile_number)}
            </p>
            <p className="text-xs text-gray-500">{displayAccount.operator}</p>
          </div>
          {accounts.length > 1 && (
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="flex-shrink-0 text-gray-500 hover:text-gray-700"
            >
              <svg
                className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Dropdown/Accordion for Other Accounts */}
      {isOpen && otherAccounts.length > 0 && (
        <div className="space-y-2 border-t border-gray-200 pt-3">
          {otherAccounts.map((account) => (
            <button
              key={account.id}
              type="button"
              onClick={() => {
                onSelect(account)
                setIsOpen(false)
              }}
              className="w-full flex items-center space-x-3 p-3 rounded-lg border border-gray-200 bg-white hover:border-primary-500 hover:bg-primary-50 transition-all"
            >
              {getNetworkLogo(account.operator)}
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-gray-900">
                  {formatPhoneNumber(account.mobile_number)}
                </p>
                <p className="text-xs text-gray-500">{account.operator}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Add New Mobile Money Form */}
      {isOpen && showAddForm && (
        <div className="border-t border-gray-200 pt-3">
          <NewMobileMoneyForm
            onSubmit={handleAddNew}
            onCancel={() => {
              setShowAddForm(false)
              setIsOpen(false)
            }}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  )
}

